import { getDatabase, onValue, ref, type Database } from 'firebase/database';
import app from './config';

let rtdb: Database | null = null;
/** Tracks whether we've already checked and failed, so we don't re-check or re-log */
let checkedAndUnavailable = false;

/**
 * Client-to-server clock skew in ms (server = client + offset). Updated live
 * via RTDB's `.info/serverTimeOffset`. Keeps all sender/receiver timestamps on
 * a single shared clock so entity interpolation cannot be fooled by classroom
 * laptop drift.
 */
let serverTimeOffsetMs = 0;
let serverTimeOffsetReady = false;
let serverTimeOffsetSubscribed = false;

function subscribeToServerTimeOffset(db: Database): void {
  if (serverTimeOffsetSubscribed) return;
  serverTimeOffsetSubscribed = true;

  const offsetRef = ref(db, '.info/serverTimeOffset');
  onValue(offsetRef, (snap) => {
    const v = snap.val();
    if (typeof v === 'number' && Number.isFinite(v)) {
      serverTimeOffsetMs = v;
      serverTimeOffsetReady = true;
      console.log(`[rtdb] server offset resolved: ${Math.round(v)}ms`);
    }
  });
}

/** Server-aligned "now". Falls back to `Date.now()` until `.info/serverTimeOffset` resolves. */
export function nowServer(): number {
  return Date.now() + serverTimeOffsetMs;
}

/** True once `.info/serverTimeOffset` has produced at least one value. */
export function isServerTimeReady(): boolean {
  return serverTimeOffsetReady;
}

/**
 * Basic validation that the RTDB URL looks like a real Firebase database URL,
 * not a placeholder from .env.example.
 */
function isValidRtdbUrl(url: string): boolean {
  // Reject obvious placeholders
  if (url.includes('<') || url.includes('>') || url.includes('your-project')) {
    return false;
  }
  // Must be a valid https URL ending in firebaseio.com or firebasedatabase.app
  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === 'https:' &&
      (parsed.hostname.endsWith('.firebaseio.com') || parsed.hostname.endsWith('.firebasedatabase.app'))
    );
  } catch {
    return false;
  }
}

/**
 * Get the Firebase Realtime Database instance.
 * Returns null if RTDB is not configured (databaseURL missing or invalid).
 */
export function getRealtimeDb(): Database | null {
  if (rtdb) return rtdb;
  if (checkedAndUnavailable) return null;

  const databaseURL = import.meta.env.VITE_FIREBASE_DATABASE_URL;
  if (!databaseURL) {
    checkedAndUnavailable = true;
    console.warn('⚠️ RTDB not configured: VITE_FIREBASE_DATABASE_URL is missing. Falling back to Firestore for arena sync.');
    return null;
  }

  if (!isValidRtdbUrl(databaseURL)) {
    checkedAndUnavailable = true;
    console.warn('⚠️ RTDB not configured: VITE_FIREBASE_DATABASE_URL looks like a placeholder or is invalid. Falling back to Firestore for arena sync.');
    return null;
  }

  try {
    rtdb = getDatabase(app, databaseURL);
    subscribeToServerTimeOffset(rtdb);
    return rtdb;
  } catch (error) {
    checkedAndUnavailable = true;
    console.error('❌ Failed to initialize Firebase Realtime Database:', error);
    return null;
  }
}
